import React, { useState, useEffect, useRef } from "react"
import {
  sanitizeInnerHtml,
  sanitizeDOMString,
  validateField,
  getShortenedUrl,
} from "@/utils/helper"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "next-i18next"
import _get from "lodash/get"

import ReCaptcha, {
  validateRecaptcha,
} from "@/components/core/ReCaptcha/ReCaptcha"
import SubscribeInfoModal from "@/components/Shared/NewsletterFooter/SubscribeInfoModal"
import styles from "@/components/Shared/NewsletterFooter/index.module.scss"
import { selectGenericState, showToast } from "@/store/features/genericSlice"
import { getTranslations } from "@/components/Shared/NewsletterFooter/localization"
import { getConfig } from "@/constants/config"
import { apim } from "@/constants/api"
import { emailSubscribeEndPoint } from "@/constants/index"

const NewsletterFooter = props => {
  const dispatch = useDispatch()
  const [validationError, setValidationError] = useState("")
  const [email, setEmail] = useState("")
  const [captchaActive, setCaptchaActive] = useState(false)
  const submitButton = useRef(null)
  const { t } = useTranslation("common")
  const [staticTexts, setStaticTexts] = useState({})
  const [siteName, setSiteName] = useState("")
  const [homePagePath, setHomePagePath] = useState("/")
  const [isShowModal, setIsShowModal] = useState(false)
  const [isResetForm, setIsResetForm] = useState(false)
  const [isAlreadyExist, setIsAlreadyExist] = useState(false)
  const { subscribeModal } = useSelector(selectGenericState)
  const { data, columnClassNames } = props

  const addNewsletterSignupAnalytics = (status, message) => {
    if (window.adobeDataLayer) {
      const { adobeDataLayer: { getState } = {} } = window
      const eventInfo = {
        "@type": siteName?.toLowerCase() + "/components/newslettersignin",
        eventMsg: message || "n/a",
        eventStatus: status,
        clickInternalLinks: "true",
        internalLinkPosition: "newslettersignin",
        internalLinkZoneName: "global footer",
        internalLinkURL: "n/a",
        internalLinkName: "newsletter submit",
        eventName: "newsletter submit",
        internalLinkType: "global footer:submit",
        eventType: "click",
        eventAction: "newsletter submit",
      }
      const page = (getState && getState("page")) || {}
      window.adobeDataLayer.push({
        event: "cmp:click",
        eventInfo: eventInfo,
        page: page,
      })
    }
  }
  useEffect(() => {
    if (isResetForm) setEmail("")
  }, [isResetForm, isAlreadyExist])

  useEffect(() => {
    setCaptchaActive(false)
    setStaticTexts(getTranslations(t))
    setInitalData()
  }, [])

  const handleModalClose = () => {
    setIsShowModal(false)
  }

  const setInitalData = async () => {
    const config = await getConfig()
    setSiteName(_get(config, "general.siteName", ""))
    setHomePagePath(
      await getShortenedUrl(_get(config, "general.homepageUrl", "/"))
    )
  }

  const getSubscriptionData = email => {
    const body = { email: email }
    validateRecaptcha()
      .then(res => {
        if (res.success) {
          apim
            .post(emailSubscribeEndPoint, body)
            .then(response => {
              if (response && response.data) {
                const isSuccess = response.data.resultMessages ?? []
                if (isSuccess) {
                  setIsShowModal(true)
                  setIsResetForm(true)
                }
                setEmail("")
                addNewsletterSignupAnalytics("success")
              } else {
                addNewsletterSignupAnalytics(
                  "failure",
                  "news letter subscription api: error occured while fetching data from get url property"
                )
                // log.error(
                //   "News letter subscription API: Error occured while fetching data from Get URL Property"
                // )
              }
            })
            .catch(err => {
              if (err.response?.data?.statusCode === 400) {
                setIsAlreadyExist(true)
                setValidationError(staticTexts?.alreadySubscribed)
                addNewsletterSignupAnalytics(
                  "failure",
                  staticTexts?.alreadySubscribed.toLowerCase()
                )
              } else {
                console.log(staticTexts.genError)
                dispatch(
                  showToast({
                    message: staticTexts.genError,
                    isVisible: true,
                  })
                )
                addNewsletterSignupAnalytics(
                  "failure",
                  staticTexts.genError.toLowerCase()
                )
              }
            })
        } else {
          dispatch(
            showToast({
              message: staticTexts.genError,
              isVisible: true,
            })
          )
          addNewsletterSignupAnalytics(
            "failure",
            staticTexts.genError.toLowerCase()
          )
        }
      })
      .catch(() => {
        dispatch(
          showToast({
            message: staticTexts.genError,
            isVisible: true,
          })
        )
        addNewsletterSignupAnalytics(
          "failure",
          staticTexts.genError.toLowerCase()
        )
      })
  }

  const onSubmit = async () => {
    if (email === "" || email == null || email?.length == 0) {
      setValidationError(staticTexts.enterEmail)
      addNewsletterSignupAnalytics(
        "failure",
        staticTexts.enterEmail.toLowerCase()
      )
    }
    if (email?.length > 0) {
      const isValid = await validateField("email", email)
      if (!isValid) {
        addNewsletterSignupAnalytics(
          "failure",
          staticTexts.enterEmail.toLowerCase()
        )
        setValidationError(staticTexts.validEmail)
      } else {
        setValidationError("")
        getSubscriptionData(email)
      }
    }
    handleFocus()
  }

  const handleFocus = () => {
    // removing focus
    submitButton.current.blur()
  }

  const handleChangeEmail = e => {
    setEmail(e.target.value)
    setIsAlreadyExist(false)
    setValidationError("")
  }

  return (
    <React.Fragment>
      <div
        className={`${columnClassNames ? columnClassNames : ""} ${
          styles.wrapper
        }`}
      >
        <footer>
          <section className="gbh-footer-newsletter-nav">
            <section className="gbh-container">
              <section
                className="gbh-footer-news-letter"
                aria-label={data?.title}
                role="list"
              >
                <h2>{data?.title}</h2>
                {sanitizeInnerHtml(data?.description)}
                <div
                  className="form-group newsletter-form-group"
                  role="listitem"
                >
                  <label htmlFor="newsletter_input">
                    <input
                      className="form-control"
                      type="text"
                      aria-label={data?.placeholder}
                      placeholder={data?.placeholder}
                      maxLength="60"
                      id="newsletter_input"
                      onChange={handleChangeEmail}
                      value={email}
                      data-testid="newsletter-input"
                    />
                  </label>
                  {data?.submit && (
                    <button
                      aria-label="Submit button. Tap or click here to subscribe for Koher newsletter to receive news and promotions."
                      className={`btn btn-secondary ${
                        email.length ? "active-btn" : ""
                      }`}
                      id="newsletter_submit"
                      onClick={() => onSubmit()}
                      ref={submitButton}
                      data-testid="newsletter-submit"
                    >
                      {data?.submit}
                    </button>
                  )}
                </div>
                <div role="listitem" className="subsribe-error-container">
                  {validationError && (
                    <span
                      id="subsribe-mail-error"
                      tabIndex="0"
                      data-testid="email-error"
                    >
                      {sanitizeDOMString(validationError)}
                    </span>
                  )}
                </div>
              </section>
            </section>
            {captchaActive && <ReCaptcha />}
          </section>
        </footer>
      </div>
      {
        <SubscribeInfoModal
          show={isShowModal}
          handleModalClose={handleModalClose}
          staticTexts={staticTexts}
          homePagePath={homePagePath}
          className={styles.wrapper}
          setEmail={setEmail}
          modalContent={subscribeModal}
        />
      }
    </React.Fragment>
  )
}

export default NewsletterFooter
